import React, { useEffect, useState } from 'react'
import { FiAlertTriangle } from 'react-icons/fi'
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md'
import AddDonorButton from '@components/Request/AddDonorButton'
import IDonor from '@interfaces/IDonor'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { SelectionDonorCard } from '@components/Cards/DonorCard'
import * as $Order from '@services/Order'
import withReactContent from 'sweetalert2-react-content'
import Swal from 'sweetalert2'
import { FaCircleQuestion } from 'react-icons/fa6'

interface DonorValidationStepProps {
  donors: IDonor[]
  setDonors: React.Dispatch<React.SetStateAction<IDonor[]>>
  selectedTreatment: string
  handleRemoveFromCart: (id: string) => void
  hasDonorWithoutEligibleTreatment: boolean
  onBack: () => void
  onNext: () => void
  onRefreshDonors?: () => void
}

const DonorValidationStep: React.FC<DonorValidationStepProps> = ({
  donors,
  setDonors,
  selectedTreatment,
  handleRemoveFromCart,
  hasDonorWithoutEligibleTreatment,
  onBack,
  onNext,
  onRefreshDonors,
}) => {
  const SweetAlert = withReactContent(Swal)

  const [showVideoModal, setShowVideoModal] = useState(false)

  const handleHelpClick = () => setShowVideoModal(true)

  const handleCloseModal = () => setShowVideoModal(false)

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleNext = async () => {
    setIsSubmitting(true)
    try {
      await onNext()
    } finally {
      setIsSubmitting(false)
    }
  }

  // Listen for when the tab regains focus (user returns from adding donors)
  useEffect(() => {
    const handleFocus = () => {
      if (onRefreshDonors) {
        onRefreshDonors()
      }
    }

    window.addEventListener('focus', handleFocus)

    return () => {
      window.removeEventListener('focus', handleFocus)
    }
  }, [onRefreshDonors])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Validate if step requirements are met
  const isStepValid = donors.length >= 1 &&
    donors.length <= 6 &&
    !hasDonorWithoutEligibleTreatment

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setDonors((items) => {
        const oldIndex = items.findIndex((item) => item.internal_id === active.id)
        const newIndex = items.findIndex((item) => item.internal_id === over.id)

        const newOrder = arrayMove(items, oldIndex, newIndex)

        // Call API to update order
        const donorIds = newOrder.map((donor) => String(donor.internal_id))
        $Order.reorderDonors(donorIds).catch((error) => {
          SweetAlert.fire('Erro', error.message || 'Erro ao reordenar doadores', 'error')
          // Revert on error
          setDonors(items)
        })

        return newOrder
      })
    }
  }

  return (
    <div className="select-donor">
      <div className="title-tratament">
        <div className="title">
          <h5>
            Doadores compatíveis com o seu tratamento
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <FaCircleQuestion style={{ color: '#FA5A84', marginLeft: '5px', cursor: 'pointer', fontSize: '14px' }} onClick={handleHelpClick} />
            </span>
          </h5>
          <p>Listamos aqui os doadores inseridos no seu carrinho e a disponibilidade de amostras deles para o seu tipo de tratamento, indicado na etapa anterior.</p>
          <p>Você só precisa de um, mas pode inserir até 6 doadores na pré-reserva, ordenando de acordo com sua preferência.</p>
        </div>
      </div>

      <div className={`alert alert-danger mt-3 mb-4 ${hasDonorWithoutEligibleTreatment ? 'd-block' : 'd-none'}`}>
        <div className="d-flex align-items-center">
          <FiAlertTriangle />
          <p className="mb-0 ml-3">É necessário remover os doadores marcados em vermelho abaixo, pois não são compatíveis com o seu tratamento indicado.</p>
        </div>
      </div>

      <div className={`alert alert-danger mt-3 mb-4 ${donors.length < 1 ? 'd-block' : 'd-none'}`}>
        <div className="d-flex align-items-center">
          <FiAlertTriangle />
          <p className="mb-0 ml-3">É necessário escolher no mínimo 1 doador para prosseguir!</p>
        </div>
      </div>

      <div className="row align-items-center">
        <div className="col-12">
          <div className="card-index mt-2">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={donors.map((d) => String(d.internal_id))}
                strategy={verticalListSortingStrategy}
              >
                {donors.map((donor, i) => (
                  <SelectionDonorCard
                    key={donor.internal_id}
                    donor={donor}
                    position={i+1}
                    selectedTreatment={selectedTreatment}
                    onRemove={handleRemoveFromCart}
                    isLast={i === donors.length - 1}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>

          {donors.length <= 5 && (
            <div className="mt-3">
              <AddDonorButton currentCount={donors.length} maxCount={6} showCounter={true} />
            </div>
          )}
        </div>
      </div>

      <div className="request-footer">
        <div className="row">
          <div className="col-12 d-flex justify-content-between">
            <button className="return-btn btn btn-primary" onClick={onBack}>
              <MdKeyboardArrowLeft />
              Voltar
            </button>
            <button
              className="next-btn btn btn-primary"
              onClick={handleNext}
              disabled={!isStepValid || isSubmitting}
            >
              {isSubmitting ? 'Enviando...' : 'Próximo passo'}
              <MdKeyboardArrowRight />
            </button>
          </div>
        </div>
      </div>
      {showVideoModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={handleCloseModal}>
          <div style={{ position: 'relative', width: 'min(1280px, 90vw)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
              <iframe style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} src="https://www.youtube.com/embed/8nFSKUkZcOE" title="YouTube video player" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen></iframe>
            </div>
            <button style={{ position: 'absolute', top: '-10px', right: '-10px', background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer' }} onClick={handleCloseModal}>×</button>
          </div>
        </div>
      )}
    </div>
  )
}

export default DonorValidationStep